/**
 * Vendor → Subcontracts tab (construction pack)
 * Lists all subcontracts with this vendor across all projects.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useQuery } from "@tanstack/react-query";
import { subcontractsListQueryOptions } from "@/lib/queries";
import type { Subcontract } from "@/lib/types";

export const Route = createFileRoute("/_auth/vendors/$vendorId/subcontracts")({
  component: VendorSubcontractsTab,
});

const STATUS_COLOR: Record<string, string> = {
  draft: "bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-slate-300",
  executed:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  completed:
    "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  void: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

function fmt(n: string | number) {
  const v = parseFloat(String(n));
  return isNaN(v)
    ? "—"
    : v.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });
}

function VendorSubcontractsTab() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;
  const { vendorId } = Route.useParams();
  const scQ = useQuery(subcontractsListQueryOptions({ vendor_id: vendorId }));
  const subcontracts: Subcontract[] = scQ.data ?? [];

  const totalOriginal = subcontracts.reduce(
    (s, sc) => s + parseFloat(sc.original_amount),
    0,
  );
  const totalWithCOs = subcontracts.reduce(
    (s, sc) =>
      s + parseFloat(sc.original_amount) + parseFloat(sc.approved_co_amount),
    0,
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Subcontracts
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {subcontracts.length} subcontract(s) ·{" "}
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {fmt(totalOriginal)} original
            </span>
            {totalWithCOs !== totalOriginal && (
              <>
                {" · "}
                <span className="font-medium text-amber-700 dark:text-amber-400">
                  {fmt(totalWithCOs)} with COs
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      {scQ.isLoading && (
        <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
      )}
      {scQ.error && (
        <div className="text-sm text-red-600">
          Failed to load: {(scQ.error as Error).message}
        </div>
      )}

      {!scQ.isLoading && subcontracts.length === 0 && (
        <div className="text-sm text-slate-400 dark:text-slate-500">
          No subcontracts on file for this vendor.
        </div>
      )}

      {subcontracts.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-gray-900 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2.5">SC #</th>
                <th className="text-left px-3 py-2.5">Status</th>
                <th className="text-left px-3 py-2.5">Description</th>
                <th className="text-left px-3 py-2.5">Executed</th>
                <th className="text-right px-3 py-2.5">Original</th>
                <th className="text-right px-3 py-2.5">+ COs</th>
                <th className="text-right px-3 py-2.5">Retention %</th>
                <th className="text-center px-3 py-2.5">Ins.</th>
                <th className="text-center px-3 py-2.5">WC</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
              {subcontracts.map((sc) => (
                <tr
                  key={sc.id}
                  className="hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <td className="px-3 py-2.5 font-mono text-slate-800 dark:text-slate-200">
                    {sc.subcontract_no}
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`px-2 py-0.5 text-xs rounded font-medium ${STATUS_COLOR[sc.status] ?? ""}`}
                    >
                      {sc.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-slate-700 dark:text-slate-300 max-w-xs truncate">
                    {sc.description ?? "—"}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-500 dark:text-slate-400">
                    {sc.executed_date ?? "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right font-medium text-slate-900 dark:text-slate-100">
                    {fmt(sc.original_amount)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-slate-600 dark:text-slate-400">
                    {parseFloat(sc.approved_co_amount) !== 0
                      ? fmt(parseFloat(sc.original_amount) + parseFloat(sc.approved_co_amount))
                      : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right text-xs text-slate-600 dark:text-slate-400">
                    {sc.retention_percent}%
                  </td>
                  <td className="px-3 py-2.5 text-center text-lg">
                    {sc.insurance_received ? "✓" : "✗"}
                  </td>
                  <td className="px-3 py-2.5 text-center text-lg">
                    {sc.workers_comp_received ? "✓" : "✗"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
